<template>
  <BaseModal :title="`Stream to ${displayName}`" @close="closeModal">
    <template #modalContent>
      <div class="spf">
        <header class="spf-head">
          <button type="button" class="spf-back" @click="closeModal">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back
          </button>
          <div class="spf-title-row">
            <span class="spf-icon" :class="platformKey" v-html="icon"></span>
            <div>
              <p class="spf-eyebrow">Live streaming</p>
              <h2 class="spf-title">{{ displayName }} stream</h2>
            </div>
          </div>
          <p class="spf-sub">
            Paste the RTMP URL and stream key from your
            {{ displayName }} broadcast dashboard.
          </p>
        </header>

        <section class="spf-fields">
          <div class="spf-field">
            <label class="spf-label"
              >RTMP URL <span class="spf-req">*</span></label
            >
            <input
              v-model="rtmpUrl"
              type="text"
              class="spf-input"
              :placeholder="rtmpPlaceholder"
              @input="urlError = ''"
            />
            <p v-if="urlError" class="spf-err">{{ urlError }}</p>
          </div>
          <div class="spf-field">
            <label class="spf-label"
              >Stream key <span class="spf-req">*</span></label
            >
            <input
              v-model="secretKey"
              :type="showKey ? 'text' : 'password'"
              class="spf-input"
              placeholder="Paste your stream key"
              @input="secretError = ''"
            />
            <div class="spf-key-row">
              <button
                type="button"
                class="spf-key-toggle"
                @click="showKey = !showKey"
              >
                {{ showKey ? 'Hide key' : 'Show key' }}
              </button>
              <p v-if="secretError" class="spf-err">{{ secretError }}</p>
            </div>
          </div>
        </section>

        <footer class="spf-actions">
          <button type="button" class="spf-btn ghost" @click="closeModal">
            Cancel
          </button>
          <button type="button" class="spf-btn primary" @click="confirm">
            Save destination
          </button>
        </footer>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/common/BaseModal.vue';
import { validateRtmpDestination } from '../CreateCastForm.vue';

const ICONS = {
  youtube:
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136C4.495 20.455 12 20.455 12 20.455s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.546 15.568V8.432L15.818 12l-6.272 3.568z"/></svg>',
  facebook:
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.017 1.792-4.683 4.533-4.683 1.312 0 2.686.235 2.686.235v2.97h-1.514c-1.491 0-1.956.93-1.956 1.886v2.262h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>',
  twitch:
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>',
  linkedin:
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
};

const PLACEHOLDERS = {
  youtube: 'rtmp://a.rtmp.youtube.com/live2',
  linkedin: 'rtmps://….live-input-li.bitcod.in/live',
  facebook: 'rtmps://live-api-s.facebook.com:443/rtmp/',
  twitch: 'rtmp://live.twitch.tv/app/',
};

const DISPLAY = {
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  twitch: 'Twitch',
};

export default {
  name: 'StreamPlatformForm',
  props: {
    platformKey: { type: String, required: true }, // 'youtube' | 'facebook' | 'twitch' | 'linkedin'
    closeModal: { type: Function, required: true },
    castDetails: { type: Object, required: true },
    stepThreeProps: { type: Object, required: true },
    handleEditCast: { type: Function, required: true },
  },
  components: { BaseModal },
  data() {
    return {
      rtmpUrl: '',
      secretKey: '',
      urlError: '',
      secretError: '',
      showKey: false,
      VWStream: false,
      youtube: '',
      youtubeSecret: '',
      facebook: '',
      facebookSecret: '',
      twitch: '',
      twitchSecret: '',
      linkedin: '',
      linkedinSecret: '',
    };
  },
  computed: {
    icon() {
      return ICONS[this.platformKey] || '';
    },
    rtmpPlaceholder() {
      return PLACEHOLDERS[this.platformKey] || 'rtmp://…';
    },
    displayName() {
      return DISPLAY[this.platformKey] || this.platformKey;
    },
  },
  mounted() {
    this.$store
      .dispatch('cast/editEvent', this.castDetails.public_meeting_id)
      .then((res) => {
        const info = res.data.details;
        if (info.vw_stream) {
          this.stepThreeProps.vw_stream = true;
          this.VWStream = true;
        }
        if (info.bbb_stream_url !== null) {
          // eslint-disable-next-line no-eval
          window.eval(info.bbb_stream_url).forEach((item) => {
            const x = item.split('/');
            if (item.match(/youtube/)) {
              this.stepThreeProps.youtube_secret_key = x.pop();
              this.stepThreeProps.youtube_rtmp_url = x.join('/');
              this.stepThreeProps.youtube_stream_url = item;
              this.stepThreeProps.record_youtube = true;
              this.youtubeSecret = this.stepThreeProps.youtube_secret_key;
              this.youtube = this.stepThreeProps.youtube_rtmp_url;
            }
            if (item.match(/facebook/)) {
              this.stepThreeProps.facebook_secret_key = x.pop();
              this.stepThreeProps.facebook_rtmp_url = x.join('/');
              this.stepThreeProps.facebook_stream_url = item;
              this.stepThreeProps.record_facebook = true;
              this.facebookSecret = this.stepThreeProps.facebook_secret_key;
              this.facebook = this.stepThreeProps.facebook_rtmp_url;
            }
            if (item.match(/twitch/)) {
              this.stepThreeProps.twitch_secret_key = x.pop();
              this.stepThreeProps.twitch_rtmp_url = x.join('/');
              this.stepThreeProps.twitch_stream_url = item;
              this.stepThreeProps.record_twich = true;
              this.twitch = this.stepThreeProps.twitch_rtmp_url;
              this.twitchSecret = this.stepThreeProps.twitch_secret_key;
            }
            // LinkedIn ingest lives on bitcod.in (Bitmovin), so match both
            if (item.match(/linkedin|bitcod/)) {
              this.stepThreeProps.linkedin_secret_key = x.pop();
              this.stepThreeProps.linkedin_rtmp_url = x.join('/');
              this.stepThreeProps.linkedin_stream_url = item;
              this.stepThreeProps.record_linkedin = true;
              this.linkedin = this.stepThreeProps.linkedin_rtmp_url;
              this.linkedinSecret = this.stepThreeProps.linkedin_secret_key;
            }
          });
        }
        // Prefill the inputs for the platform this modal represents.
        if (this.platformKey === 'youtube') {
          this.rtmpUrl = this.youtube || '';
          this.secretKey = this.youtubeSecret || '';
        } else if (this.platformKey === 'facebook') {
          this.rtmpUrl = this.facebook || '';
          this.secretKey = this.facebookSecret || '';
        } else if (this.platformKey === 'twitch') {
          this.rtmpUrl = this.twitch || '';
          this.secretKey = this.twitchSecret || '';
        } else if (this.platformKey === 'linkedin') {
          this.rtmpUrl = this.linkedin || '';
          this.secretKey = this.linkedinSecret || '';
        }
      })
      .catch((e) => {
        console.log('Error editing', e);
      });
  },
  methods: {
    confirm() {
      const url = (this.rtmpUrl || '').trim();
      const key = (this.secretKey || '').trim();
      const errs = validateRtmpDestination(url, key, this.platformKey);
      this.urlError = errs.url;
      this.secretError = errs.key;
      if (this.urlError || this.secretError) return;
      this.rtmpUrl = url;
      this.secretKey = key;

      // Push the values back into the matching pair before composing
      // the urls list.
      if (this.platformKey === 'youtube') {
        this.youtube = this.rtmpUrl;
        this.youtubeSecret = this.secretKey;
      } else if (this.platformKey === 'facebook') {
        this.facebook = this.rtmpUrl;
        this.facebookSecret = this.secretKey;
      } else if (this.platformKey === 'twitch') {
        this.twitch = this.rtmpUrl;
        this.twitchSecret = this.secretKey;
      } else if (this.platformKey === 'linkedin') {
        this.linkedin = this.rtmpUrl;
        this.linkedinSecret = this.secretKey;
      }

      const streamUrls = [
        { vw_stream: this.VWStream ? 'True' : 'False' },
        { urls: [] },
      ];
      if (this.youtube && this.youtubeSecret) {
        streamUrls[1].urls.push(`${this.youtube}/${this.youtubeSecret}`);
      }
      if (this.facebook && this.facebookSecret) {
        streamUrls[1].urls.push(`${this.facebook}/${this.facebookSecret}`);
      }
      if (this.twitch && this.twitchSecret) {
        streamUrls[1].urls.push(`${this.twitch}/${this.twitchSecret}`);
      }
      if (this.linkedin && this.linkedinSecret) {
        streamUrls[1].urls.push(`${this.linkedin}/${this.linkedinSecret}`);
      }
      const isStreaming = this.VWStream || streamUrls[1].urls.length > 0;
      this.stepThreeProps.is_streaming = isStreaming;
      this.stepThreeProps.vw_stream = streamUrls[0].vw_stream;
      this.stepThreeProps.vw_stream_url = JSON.stringify(streamUrls);
      this.handleEditCast({ updateStream: true });
    },
  },
};
</script>

<style scoped>
.spf {
  width: 520px;
  max-width: calc(100vw - 32px);
  padding: 10px 6px 4px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  color: #fff;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}
.spf-head {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.spf-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  align-self: flex-start;
}
.spf-back:hover {
  color: #fff;
}
.spf-title-row {
  display: flex;
  align-items: center;
  gap: 14px;
}
.spf-icon {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.04);
}
.spf-icon.youtube {
  color: #ff0033;
}
.spf-icon.facebook {
  color: #1877f2;
}
.spf-icon.twitch {
  color: #9146ff;
}
.spf-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}
.spf-title {
  font-size: 20px;
  font-weight: 600;
  margin: 2px 0 0;
  letter-spacing: -0.005em;
}
.spf-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
  line-height: 1.5;
}

.spf-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.spf-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.spf-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
}
.spf-req {
  color: #d7df23;
}
.spf-input {
  width: 100%;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 12px;
  color: #fff;
  font-size: 13px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s ease;
}
.spf-input:focus {
  border-color: rgba(215, 223, 35, 0.55);
}
.spf-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}
.spf-key-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.spf-key-toggle {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  font-size: 11px;
  font-family: inherit;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.spf-key-toggle:hover {
  color: #fff;
}
.spf-err {
  font-size: 12px;
  color: #f87171;
  margin: 0;
}

.spf-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 4px;
}
.spf-btn {
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.spf-btn.ghost {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.75);
}
.spf-btn.ghost:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.25);
}
.spf-btn.primary {
  background: #d7df23;
  color: #0b0c14;
}
.spf-btn.primary:hover {
  background: #e7ef33;
}

@media (max-width: 540px) {
  .spf {
    width: 100%;
  }
  .spf-actions {
    flex-direction: column-reverse;
  }
  .spf-btn {
    width: 100%;
  }
}
</style>
